import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { PurchaseOrderApprovalStatus } from '../../enums/purchase-order-approval-status.enum';
import { BaseEntity } from './base.entity';
import { CompanyEntity } from './company.entity';
import type { PurchaseOrderApprovalEventEntity } from './purchase-order-approval-event.entity';
import { PurchaseOrderDetailEntity } from './purchase-order-detail.entity';
import type { PurchaseOrderFileEntity } from './purchase-order-file.entity';
import { VendorEntity } from './vendor.entity';
import { WarehouseEntity } from './warehouse.entity';

@Index('externalId_fk_company__id', ['externalId', 'fkCompanyId'], {
	unique: true,
})
@Index('fk_company__id', ['fkCompanyId'], {})
@Index('purchase_order__fk_vendor__id_fk', ['fkVendorId'], {})
@Index(
	'purchase_order_externalId_fk_company__id__deleted_userId',
	['deletedUserId', 'externalId', 'fkCompanyId'],
	{},
)
@Entity('purchase_order')
export class PurchaseOrderEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', nullable: true, length: 64 })
	public externalId!: string | null;

	@Column('datetime', { name: 'date', nullable: true })
	public date!: Date | null;

	@Column('datetime', { name: 'requestedDate', nullable: true })
	public requestedDate!: Date | null;

	@Column('varchar', { name: 'status', nullable: true, length: 32 })
	public status!: string | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('int', { name: '_fk_vendor__id', nullable: true, unsigned: true })
	public fkVendorId!: number | null;

	// PD-14575: the PO's destination (ship-to) warehouse. One PO = one
	// warehouse (industry standard; EDI 850 ship-to is header-level).
	// Additive + nullable so the database sync applies it non-destructively;
	// existing POs stay NULL and receiving falls back to the tenant's sole
	// active warehouse when there is exactly one.
	@Index('purchase_order__fk_warehouse__id_fk', ['fkWarehouseId'], {})
	@Column('int', { name: 'fk_warehouse__id', nullable: true, unsigned: true })
	public fkWarehouseId!: number | null;

	// PD-14666: denormalized count of OPEN receipt exceptions (status not
	// RESOLVED / WRITTEN_OFF) across this PO's receipts, maintained by the
	// exception recompute path on every exception create / transition and
	// on receipt delete. Deliberately NULL — not 0 — when there are none:
	// the shared filter registry expresses "has open issues" through its
	// IS NOT NULL filter kind (joinType 'exists' + fieldName), so NULL
	// semantics make the Outstanding Vendor Issues tab a plain registry
	// filter instead of requiring a new EXISTS-subquery filter type.
	@Index('purchase_order__openExceptionCount_idx', ['openExceptionCount'], {})
	@Column('int', {
		name: 'openExceptionCount',
		nullable: true,
		unsigned: true,
	})
	public openExceptionCount!: number | null;

	@Column('int', {
		name: 'fk_company__id',
		unsigned: true,
		default: () => "'1'",
	})
	public fkCompanyId!: number;

	// PO ↔ SR relationship lives at the line level on
	// `purchase_order_detail.fk_sourcing_request_line__id`. A PO is a
	// vendor contract that can span lines from multiple SRs, so a
	// header-level FK here would be wrong — it forced one PO per SR
	// and prevented consolidation. To find the SRs related to a PO,
	// query distinct `_fk_sourcing_request__id` from the joined
	// sourcing_request_line rows.

	@Column('int', { name: 'rfqRoundNumber', nullable: true, unsigned: true })
	public rfqRoundNumber!: number | null;

	@Column('datetime', { name: 'expirationDate', nullable: true })
	public expirationDate!: Date | null;

	@Column('datetime', { name: 'followUpDate', nullable: true })
	public followUpDate!: Date | null;

	@Column('varchar', {
		name: 'vendorContactEmailOverride',
		nullable: true,
		length: 255,
	})
	public vendorContactEmailOverride!: string | null;

	// ── Approval gate (orthogonal to lifecycle `status`) ──
	// `null` = no approval required for this PO (default; rules in
	// the tenant's `purchasing-approval-check` preference didn't
	// trip when Submit-for-Approval was attempted). Otherwise tracks
	// the gate state. See `PurchaseOrderApprovalStatus` for the full
	// state-machine docstring; in short, `submitted` locks the PO
	// from edits + Release until a Purchasing Manager runs Approve
	// or Reject. Audit trail lives in `purchase_order_approval_event`.
	@Index('purchase_order__approval_status_idx', ['approvalStatus'], {})
	@Column('varchar', { name: 'approvalStatus', nullable: true, length: 16 })
	public approvalStatus!: PurchaseOrderApprovalStatus | null;

	// JSON array of `{ type, thresholdAmount?, ... }` rule snapshots
	// captured at submit time so the manager UI can render "why
	// does this need approval?" without re-running the rule check.
	// Mirrors `order.approvalRequiredReason` shape.
	@Column('longtext', {
		name: 'approvalRequiredReason',
		nullable: true,
	})
	public approvalRequiredReason!: string | null;

	@Column('int', {
		name: '_fk_user__id_approver',
		nullable: true,
		unsigned: true,
	})
	public fkApproverUserId!: number | null;

	@Column('datetime', { name: 'approvedDate', nullable: true })
	public approvedDate!: Date | null;

	// Reason captured by the manager on Reject. Visible to the
	// submitter so they know what to fix before resubmitting.
	@Column('text', { name: 'rejectionReason', nullable: true })
	public rejectionReason!: string | null;

	@ManyToOne(() => CompanyEntity, (company) => company.purchaseOrders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;

	@ManyToOne(() => VendorEntity, (vendor) => vendor.purchaseOrders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_vendor__id', referencedColumnName: 'id' }])
	public fkVendor!: VendorEntity;

	// PD-14575: destination warehouse relation (see fkWarehouseId above).
	@ManyToOne(() => WarehouseEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_warehouse__id', referencedColumnName: 'id' }])
	public fkWarehouse!: WarehouseEntity | null;

	@OneToMany(
		() => PurchaseOrderDetailEntity,
		(purchaseOrderDetail) => purchaseOrderDetail.fkPurchaseOrder,
		{},
	)
	public purchaseOrderDetails!: Array<PurchaseOrderDetailEntity>;

	@OneToMany(
		'PurchaseOrderFileEntity',
		(purchaseOrderFile: PurchaseOrderFileEntity) =>
			purchaseOrderFile.fkPurchaseOrder,
		{},
	)
	public purchaseOrderFiles!: Array<PurchaseOrderFileEntity>;

	@OneToMany(
		'PurchaseOrderApprovalEventEntity',
		(approvalEvent: PurchaseOrderApprovalEventEntity) =>
			approvalEvent.fkPurchaseOrder,
		{},
	)
	public approvalEvents!: Array<PurchaseOrderApprovalEventEntity>;
}
